// Main-thread bridge to the synth's AudioWorklet processor.
//
// Public API:
//   - new Synth() and Synth.create() — async factory that handles the full
//     load + handshake.
//   - synth.start() — resumes the AudioContext (must be called from a user
//     gesture).
//   - synth.noteOn / noteOff
//   - synth.setParam(name, value), setDiscrete(name, value)
//   - synth.sampleTap() — returns the most recent N samples from the ring.
//   - synth.dispose()

import {
  type MainToWorklet,
  type WorkletToMain,
  RING_CAPACITY,
  RING_TOTAL_BYTES,
  ringIndexView,
  ringSamplesView,
} from "./protocol.ts";

// The worklet is pre-bundled by `pnpm build:worklet` into `public/worklet.js`
// and served as a static asset. Vite's dev-mode transforms inject an HMR
// client + `env.mjs` import that crashes AudioWorkletGlobalScope, so we
// can't use Vite's `?worker&url` here — the worklet must be untouched ES.
//
// Cache buster: AudioWorklet caches loaded modules globally per URL, and
// some browser tabs hang onto an old version across HMR. Adding a query
// param forces a fresh fetch.
const processorUrl = `/worklet.js?t=${import.meta.env.DEV ? Date.now() : "prod"}`;

// The wasm-bindgen-emitted WASM binary URL. `?url` returns Vite's
// dev-time URL in development and the hashed asset path in production.
import synthWasmUrl from "../wasm/synth-engine/synth_engine_bg.wasm?url";

export interface SynthOptions {
  /** Override the audio context's sample rate. Defaults to whatever the
   *  AudioContext picks. */
  sampleRate?: number;
}

export class Synth {
  private ctx: AudioContext;
  private node: AudioWorkletNode;
  private ringIdx: Int32Array;
  private ringSamples: Float32Array;
  private readyPromise: Promise<void>;
  private resolveReady!: () => void;
  private rejectReady!: (err: Error) => void;

  /** Wall-clock time of the most recent worklet heartbeat — paired with
   *  the worklet's process()-call count to compute the actual render
   *  rate and detect audio-thread drops. */
  private lastHeartbeatMs = 0;

  private constructor(ctx: AudioContext, node: AudioWorkletNode, ringBuffer: SharedArrayBuffer) {
    this.ctx = ctx;
    this.node = node;
    this.ringIdx = ringIndexView(ringBuffer);
    this.ringSamples = ringSamplesView(ringBuffer);
    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.resolveReady = resolve;
      this.rejectReady = reject;
    });
    this.node.port.onmessage = (ev) => this.handleMessage(ev.data as WorkletToMain);
  }

  /** Async factory. Loads the worklet module, fetches the WASM bytes,
   *  performs the init handshake, and returns the configured Synth. The
   *  caller still must invoke `synth.start()` from a user gesture before
   *  audio plays. */
  static async create(opts: SynthOptions = {}): Promise<Synth> {
    if (!self.crossOriginIsolated) {
      throw new Error(
        "cross-origin isolation is required for SharedArrayBuffer. " +
          "Check that COOP/COEP headers are being served.",
      );
    }

    const ctx = new AudioContext(opts.sampleRate ? { sampleRate: opts.sampleRate } : undefined);
    // Suspend immediately — only resume after a user gesture.
    if (ctx.state === "running") {
      await ctx.suspend();
    }

    await ctx.audioWorklet.addModule(processorUrl);

    const wasmResp = await fetch(synthWasmUrl);
    if (!wasmResp.ok) {
      throw new Error(`failed to fetch synth wasm: ${wasmResp.status}`);
    }
    const wasmBytes = await wasmResp.arrayBuffer();

    const ringBuffer = new SharedArrayBuffer(RING_TOTAL_BYTES);

    const node = new AudioWorkletNode(ctx, "synth-processor", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    node.connect(ctx.destination);

    const synth = new Synth(ctx, node, ringBuffer);

    // Send the init message. The worklet replies with "ready" once WASM is
    // instantiated.
    const initMsg: MainToWorklet = {
      type: "init",
      wasmBytes,
      ringBuffer,
      ringCapacity: RING_CAPACITY,
    };
    node.port.postMessage(initMsg, [wasmBytes]);

    // 5-second timeout in case the worklet never replies.
    await synth.waitReady(5_000);
    return synth;
  }

  private handleMessage(msg: WorkletToMain): void {
    if (msg.type === "ready") {
      this.resolveReady();
    } else if (msg.type === "error") {
      console.error("[worklet]", msg.message);
      this.rejectReady(new Error(msg.message));
    } else if (msg.type === "heartbeat" && import.meta.env.DEV) {
      // Compute the actual render rate vs the expected. The worklet
      // tells us how many process() calls it made since its last
      // heartbeat; we pair that with our own wall-clock delta. A
      // shortfall (actual < expected) means the audio thread is
      // dropping render attempts — likely CPU exhaustion, the symptom
      // the user perceives as "audio cuts out while the meter still
      // moves." The main thread's performance.now() is reliable, even
      // though the worklet's isn't.
      //
      // Gated behind import.meta.env.DEV: in production builds Vite
      // replaces the constant with `false` and the entire branch
      // dead-code-eliminates. The worklet's matching `if (__DEV__)`
      // also stops sending heartbeat messages in prod, so this branch
      // is unreachable from both ends — no console output ever.
      const now = performance.now();
      if (this.lastHeartbeatMs > 0) {
        const wallDeltaSec = (now - this.lastHeartbeatMs) / 1000;
        const expectedRate = msg.sampleRate / msg.quantumSize;
        const actualRate = msg.processCallsSinceLast / wallDeltaSec;
        const pct = (actualRate / expectedRate) * 100;
        if (pct < 95) {
          const tag = pct >= 80 ? "marginal" : "drops";
          console.warn(
            `[worklet heartbeat] render rate ${actualRate.toFixed(1)} / ${expectedRate.toFixed(1)} Hz (${pct.toFixed(1)}%, ${tag})`,
          );
        }
      }
      this.lastHeartbeatMs = now;
    }
  }

  private waitReady(timeoutMs: number): Promise<void> {
    return Promise.race([
      this.readyPromise,
      new Promise<void>((_, reject) =>
        setTimeout(
          () => reject(new Error(`worklet did not become ready within ${timeoutMs}ms`)),
          timeoutMs,
        ),
      ),
    ]);
  }

  /** Resume the AudioContext. Must be called from a user-gesture event. */
  async start(): Promise<void> {
    if (this.ctx.state !== "running") {
      await this.ctx.resume();
    }
  }

  /** Suspend the AudioContext (the synth keeps state but produces no audio). */
  async pause(): Promise<void> {
    if (this.ctx.state === "running") {
      await this.ctx.suspend();
    }
  }

  noteOn(midi: number, velocity = 1.0): void {
    this.send({ type: "note-on", midi, velocity });
  }

  /** Release the voice playing `midi`. In mono mode, if other notes are
   *  still held, the engine transitions legato to the next held note
   *  instead of releasing. */
  noteOff(midi: number): void {
    this.send({ type: "note-off", midi });
  }

  /** Release every held note. Used on mode switches and MIDI player
   *  loop boundaries. */
  allNotesOff(): void {
    this.send({ type: "all-notes-off" });
  }

  /** Set a continuous parameter by dotted name (e.g. `"filter.cutoff_hz"`). */
  setParam(name: string, value: number): void {
    this.send({ type: "param", name, value });
  }

  /** Set a discrete parameter (enum-valued or integer-valued). */
  setDiscrete(name: string, value: number): void {
    this.send({ type: "discrete", name, value });
  }

  /** Returns the most recent `n` mono samples written by the worklet, in
   *  oldest-to-newest order. Used by the scope and (later) the visualizer. */
  sampleTap(n: number): Float32Array {
    const count = Math.min(n, RING_CAPACITY);
    const out = new Float32Array(count);
    const writeHead = Atomics.load(this.ringIdx, 0);
    const start = (writeHead - count + RING_CAPACITY) % RING_CAPACITY;
    // Copy in two segments if the read wraps the end of the ring.
    const firstSegLen = Math.min(count, RING_CAPACITY - start);
    out.set(this.ringSamples.subarray(start, start + firstSegLen), 0);
    if (firstSegLen < count) {
      out.set(this.ringSamples.subarray(0, count - firstSegLen), firstSegLen);
    }
    return out;
  }

  get sampleRate(): number {
    return this.ctx.sampleRate;
  }

  get contextState(): AudioContextState {
    return this.ctx.state;
  }

  /** Diagnostic accessors — used by tests and devtools to inspect the
   *  audio path without exposing the internals through the public API. */
  get debug(): {
    ctx: AudioContext;
    node: AudioWorkletNode;
    writeHead: number;
  } {
    return {
      ctx: this.ctx,
      node: this.node,
      writeHead: Atomics.load(this.ringIdx, 0),
    };
  }

  async dispose(): Promise<void> {
    this.node.disconnect();
    await this.ctx.close();
  }

  private send(msg: MainToWorklet): void {
    this.node.port.postMessage(msg);
  }
}
